import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Eye, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { downloadSkippedNumbers, useSkippedNumbers } from '../api';

const PAGE_SIZE = 10;
/** Indian digit grouping — counts here run into lakhs (1,25,000). */
const fmt = (n: number) => n.toLocaleString('en-IN');

interface Props {
  campaignId: number | string;
  count: number;
}

/** Download + eye (view in modal) buttons shown beside the Skipped count. */
export function SkippedNumbersButton({ campaignId, count }: Props) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState(''); // debounced, digits only
  const [downloading, setDownloading] = useState(false);
  // Hovering / focusing the eye icon starts loading page 1 in the background,
  // so the modal opens with its data already there (no "Loading…" flash).
  const [warm, setWarm] = useState(false);

  // Debounce the search box; a new search always starts from page 1.
  useEffect(() => {
    const next = search.replace(/\D/g, '');
    if (next === q) return;
    const t = setTimeout(() => {
      setQ(next);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search, q]);

  const query = useSkippedNumbers(campaignId, page, PAGE_SIZE, q, open || warm);
  const data = query.data;
  const total = data?.total ?? count;
  const matched = data?.matched ?? total;
  const pages = Math.max(Math.ceil(matched / PAGE_SIZE), 1);
  const from = matched === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, matched);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadSkippedNumbers(campaignId);
    } catch {
      toast.error('Skipped numbers file is not available for this campaign.');
    } finally {
      setDownloading(false);
    }
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-5 w-5 text-muted-foreground hover:text-foreground"
        onClick={(e) => {
          stop(e);
          void handleDownload();
        }}
        disabled={downloading}
        aria-label="Download skipped numbers"
        title="Download skipped numbers"
      >
        {downloading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Download className="h-3 w-3" />
        )}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-5 w-5 text-muted-foreground hover:text-foreground"
        onMouseEnter={() => setWarm(true)}
        onFocus={() => setWarm(true)}
        onClick={(e) => {
          stop(e);
          setPage(1);
          setSearch('');
          setQ('');
          setOpen(true);
        }}
        aria-label="View skipped numbers"
        title="View skipped numbers"
      >
        <Eye className="h-3 w-3" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" onClick={stop}>
          <DialogHeader>
            <DialogTitle>Skipped numbers — campaign #{campaignId}</DialogTitle>
            <DialogDescription>
              {fmt(total)} number{total === 1 ? '' : 's'} skipped because a TTS variable was longer
              than 30 characters.
            </DialogDescription>
          </DialogHeader>

          {data && !data.available ? (
            <p className="py-6 text-sm text-muted-foreground">
              The list of skipped numbers was not saved for this campaign (it was uploaded before
              this feature). Only the count is available.
            </p>
          ) : (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search mobile number…"
                  inputMode="numeric"
                  className="h-9 pl-9 pr-9"
                  aria-label="Search skipped numbers"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Fixed-height body: the modal never jumps between pages / states. */}
              <div className="min-h-[23.5rem]">
                {query.isLoading ? (
                  <p className="py-6 text-sm text-muted-foreground">Loading…</p>
                ) : query.isError ? (
                  <p className="py-6 text-sm text-destructive">Could not load skipped numbers.</p>
                ) : (data?.rows ?? []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {q ? `No skipped number matches “${q}”.` : 'No skipped numbers.'}
                  </p>
                ) : (
                  <div
                    className={`max-h-[60vh] overflow-auto rounded-md border border-border transition-opacity duration-150 ${
                      query.isFetching ? 'opacity-60' : 'opacity-100'
                    }`}
                  >
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-muted">
                        <tr>
                          <th className="w-14 px-3 py-2 text-left font-medium">#</th>
                          {(data?.headers ?? []).map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-medium">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(data?.rows ?? []).map((r, i) => (
                          <tr key={from + i} className="border-t border-border">
                            <td className="px-3 py-1.5 text-muted-foreground">{from + i}</td>
                            {r.map((c, j) => (
                              <td key={j} className="break-all px-3 py-1.5 font-mono text-xs">
                                {c}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  Showing {fmt(from)}–{fmt(to)} of {fmt(matched)}
                  {q && matched !== total ? ` (filtered from ${fmt(total)})` : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || query.isFetching}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="whitespace-nowrap text-sm text-muted-foreground">
                    Page {fmt(page)} / {fmt(pages)}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= pages || query.isFetching}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}